import { useCallback, useEffect, useMemo, useState } from 'react';
import { StateValues } from 'react-use-form-state';
import {
  AdminApi,
  AdminInvestmentCreationRequest,
  AdminInvestorList,
  AdminSignedDocumentCreationRequest,
  ComplianceSubjectEnum,
  DocumentsApi,
  KycStatusEnum,
  Wallet,
} from 'api';
import useApiCall from 'hooks/use-api-call';
import { useTranslateWithStringArgs } from 'src/ui/hooks/use-translate';
import { toMoney, toNumber } from 'src/ui/helper/money';
import { AddInvestmentFields } from '.';
import { FileWithDocType } from 'src/ui/atoms/file-upload';
import { FileIndexed } from 'src/ui/atoms/file-upload/hooks/use-multiple-single-file-uploads';
import { debounce } from 'lodash';
import { snakeToCamelCaseWithNumbers } from 'src/ui/helper/case-transforms';

export const useAddInvestment = (
  campaignId: string,
  documentsUploaded: FileIndexed[],
  onAddNewInvestment: () => void,
  selectedInvestorId?: string,
) => {
  const { makeAuthenticatedApi, withApi, error: serverError } = useApiCall();

  const [investors, setInvestors] = useState<AdminInvestorList[]>([]);
  const [wallets, setWallets] = useState<Wallet[]>([]);
  const [error, setError] = useState('');

  const api: AdminApi = useMemo(() => makeAuthenticatedApi(AdminApi), [makeAuthenticatedApi]);
  const documentsApi: DocumentsApi = useMemo(() => makeAuthenticatedApi(DocumentsApi), [makeAuthenticatedApi]);

  const translate = useTranslateWithStringArgs();

  const searchInvestors = useCallback(
    (value: string, action?: string) => {
      if (action === 'menu-close' && value === '') {
        return;
      }

      withApi(async () => {
        const { results } = await api.adminInvestorsList({ search: value });
        const onlyKycSuccessInvestors = results?.filter((investor) => investor.kycStatus === KycStatusEnum.SUCCESS);

        setInvestors(onlyKycSuccessInvestors);
      });
    },
    [withApi, api],
  );

  const getInvestorWallets = useCallback(() => {
    withApi(async () => {
      if (selectedInvestorId) {
        const { wallets } = await api.adminInvestorsRetrieve({ id: selectedInvestorId });
        setWallets(wallets);
      }
    });
  }, [withApi, api, selectedInvestorId]);

  useEffect(() => {
    getInvestorWallets();
  }, [selectedInvestorId]);

  useEffect(() => {
    searchInvestors('');
  }, [searchInvestors]);

  const debouncedSearch = debounce(searchInvestors, 500);

  const getDocumentTypes = useCallback(() => {
    return Object.values(ComplianceSubjectEnum).map((documentType) => {
      return {
        label: translate(`documentTypes.${snakeToCamelCaseWithNumbers(documentType)}`),
        value: documentType,
      };
    });
  }, []);

  const uploadFiles = useCallback(async (documents: FileWithDocType[]) => {
    let documentsRequest: Array<AdminSignedDocumentCreationRequest> = [];

    await Promise.all(
      documents.map(async (document: FileWithDocType) => {
        if (!document.documentType || !document.file) {
          documentsRequest = [];
          return documentsRequest;
        }

        const documentResponse = await documentsApi.adminDocumentsCreate({ file: document.file });
        documentsRequest.push({
          documentId: documentResponse.id,
          complianceSubject: document.documentType as ComplianceSubjectEnum,
        });
      }),
    );

    return documentsRequest;
  }, []);

  const validate = useCallback((fieldsValues: StateValues<AddInvestmentFields>, documents: FileWithDocType[]) => {
    let errorMessage = '';

    if (!fieldsValues.investor) {
      errorMessage = translate('addInvestment.errors.noInvestor');
      return errorMessage;
    } else if (fieldsValues.acceptedAt < fieldsValues.signedAt) {
      errorMessage = translate('addInvestment.errors.acceptedDate');
      return errorMessage;
    } else if (fieldsValues.paidAt < fieldsValues.acceptedAt) {
      errorMessage = translate('addInvestment.errors.paidDate');
      return errorMessage;
    } else if (fieldsValues.signedAt > fieldsValues.paidAt) {
      errorMessage = translate('addInvestment.errors.signedAt');
      return errorMessage;
    } else if (documents?.length && documents.some((document) => !document.file)) {
      errorMessage = translate('form.errors.required.file');
      return errorMessage;
    } else if (documents?.length && documents.some((document) => !document.documentType)) {
      errorMessage = translate('addInvestment.errors.noDocumentType');
      return errorMessage;
    }

    const checkedDocumentTypes = new Set();

    const documentTypesToSubmit = [...documents.map((doc) => doc.documentType)];

    documentTypesToSubmit.some((documentType) => {
      if (checkedDocumentTypes.has(documentType)) {
        errorMessage = translate('addInvestmentDocuments.errors.multipleDocsForDocType');
        return true;
      }

      checkedDocumentTypes.add(documentType);
      return false;
    });

    return errorMessage;
  }, []);

  const onSubmit = useCallback(
    async (fieldsValues: StateValues<AddInvestmentFields>) => {
      setError('');

      const documents = documentsUploaded.filter((doc) => doc.document.file).map((doc) => doc.document);

      const fieldsValidation = validate(fieldsValues, documents);
      if (fieldsValidation.length > 0) {
        setError(fieldsValidation);
        return;
      }

      const params: AdminInvestmentCreationRequest = {
        investorId: fieldsValues.investor,
        walletId: fieldsValues.walletId,
        units: toNumber(fieldsValues.units),
        signedAt: fieldsValues.signedAt,
        paidAt: fieldsValues.paidAt,
        acceptedAt: fieldsValues.acceptedAt,
      };

      if (fieldsValues.disagio) {
        params.disagio = toMoney(fieldsValues.disagio);
      }

      if (fieldsValues.exemptionOrderAmount) {
        params.exemptionOrderAmount = toMoney(fieldsValues.exemptionOrderAmount);
      }

      if (documents) {
        params.signedDocuments = await uploadFiles(documents);
      }

      withApi(async () => {
        await api.adminCampaignsInvestmentsCreate({ id: campaignId, adminInvestmentCreationRequest: params });
        onAddNewInvestment();
      });
    },
    [JSON.stringify(documentsUploaded)],
  );

  return {
    investors,
    setError,
    validationError: error,
    getDocumentTypes,
    onSubmit,
    debouncedSearch,
    wallets,
    serverError,
  };
};
